import {
  Icons,
  LayoutCard,
  Stack,
  StatusChipV2,
  StatusChipV2Props,
  Typography,
} from '@cast/design-system';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { KarpenterLabelChip } from '@cast/split-me/features/kent';
import { notReachable } from '@cast/utils';

import { useAutoOptimization } from '../../../../_hooks/useAutoOptimization';
import { RebalancingPlanFlowState } from '../../../../_types';

import { ScheduleRebalanceSection } from './ScheduleRebalanceSection';
import { RebalancingPlanFlowSection } from './RebalancingPlanFlowSection';

type SectionStatus = 'error' | 'loading' | 'ready';

const getRebalancingPlanFlowSectionStatus = (
  rebalancingPlanFlowState: RebalancingPlanFlowState
): SectionStatus => {
  switch (rebalancingPlanFlowState.type) {
    case 'generating-plan':
    case 'idle':
    case 'nodes-eligibility-check':
      return 'loading';
    case 'no-eligible-nodes':
    case 'plan-generated':
      return 'ready';
    case 'nodes-eligibility-check-error':
    case 'plan-generation-error':
      return 'error';
    default:
      return notReachable(rebalancingPlanFlowState);
  }
};

const StatusToConfigMap: Record<
  SectionStatus,
  Pick<StatusChipV2Props, 'label' | 'color' | 'loading'>
> = {
  error: { label: 'Error', color: 'error' },
  loading: { label: 'Generating', color: 'neutral', loading: true },
  ready: { label: 'Ready', color: 'success' },
};

export const NodeAutoscalerSection = () => {
  const { rebalancingPlanFlowState } = useAutoOptimization();
  const { isKentManaged } = useCluster();

  const rebalancingPlanFlowSectionStatus = getRebalancingPlanFlowSectionStatus(
    rebalancingPlanFlowState
  );

  return (
    <LayoutCard
      headerEndContent={
        <StatusChipV2
          {...StatusToConfigMap[rebalancingPlanFlowSectionStatus]}
        />
      }
      title={
        <Stack direction="row" alignItems="center" gap={8}>
          <Icons.CastCostOptimization size={20} variant="preset" />
          <Typography variant="h6">Node autoscaler</Typography>
          {isKentManaged && <KarpenterLabelChip size="m" />}
        </Stack>
      }
      headerDivider
      padding={false}
      slotProps={{
        content: {
          sx: { p: 16 },
        },
      }}
    >
      <Stack gap={16}>
        <Stack direction="row" alignItems="center" gap={8}>
          <Icons.CastRebalancer size={18} />
          <Typography variant="A2">Rebalancer</Typography>
        </Stack>
        <RebalancingPlanFlowSection />
        <ScheduleRebalanceSection />
      </Stack>
    </LayoutCard>
  );
};
